import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { useAuth0 } from '@auth0/auth0-react';
import { Pin, ExternalLink, Activity, Zap, ChevronDown, ChevronUp, FileText } from 'lucide-react';
import { Button, buttonVariants } from '@/client/components/ui/button';
import { Badge } from '@/client/components/ui/badge';
import { cn } from '@/client/lib/utils';
import { useAlertStore } from '@/client/store/alertStore';
import { API_BASE_URL } from '@/client/config';
import type { EncoderWSSRecord } from '@/client/store/encoderWSSStore';
import { formatDistanceToNow, format } from 'date-fns';

interface EncoderMonitorCardProps {
  encoder: EncoderWSSRecord;
  onExport?: (encoderId: string) => void;
}

export function EncoderMonitorCard({ encoder, onExport }: EncoderMonitorCardProps) {
  const { getAccessTokenSilently } = useAuth0();
  const [isPinning, setIsPinning] = useState(false);
  const [confirmingPin, setConfirmingPin] = useState(false);
  const [showEventLog, setShowEventLog] = useState(false);

  const triageSessions = useAlertStore((state) => state.triageSessions);
  const addTriageSession = useAlertStore((state) => state.addTriageSession);
  const isInTriage = triageSessions.some((s) => s.encoderId === encoder.encoderID);

  // Count cycles in the event window
  const cycleCount = encoder.events.reduce((count, event, i, arr) => {
    if (i === 0) return count;
    if (arr[i - 1].type === 'disconnect' && event.type === 'connect') {
      return count + 1;
    }
    return count;
  }, 0);

  const handlePinToTriage = async () => {
    try {
      setIsPinning(true);
      const token = await getAccessTokenSilently();
      const response = await fetch(`${API_BASE_URL}/api/encoders/${encoder.encoderID}/triage`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ notes: 'Pinned from Encoder Monitoring (WSS flapping detected)' }),
      });

      if (!response.ok) throw new Error('Failed to start triage');

      const data = await response.json();
      addTriageSession(data.session);
    } catch (err) {
      console.error('Failed to pin encoder to triage:', err);
    } finally {
      setIsPinning(false);
      setConfirmingPin(false);
    }
  };

  const stateStyles = {
    alert: 'border-l-red-500 bg-red-50/10',
    warning: 'border-l-amber-500 bg-amber-50/10',
    normal: 'border-l-gray-300 bg-gray-50/10',
  };

  const stateDotStyles = {
    alert: 'bg-red-500',
    warning: 'bg-amber-500',
    normal: 'bg-gray-400',
  };

  const stateBadgeStyles = {
    alert: 'bg-red-100 text-red-700 border-red-200',
    warning: 'bg-amber-100 text-amber-700 border-amber-200',
    normal: 'bg-gray-100 text-gray-700 border-gray-200',
  };

  const stateLabels = {
    alert: 'Flapping',
    warning: 'Warning',
    normal: 'Normal',
  };

  return (
    <div
      className={cn(
        'rounded-lg border border-l-4 p-3 transition-colors',
        stateStyles[encoder.state]
      )}
    >
      <div className="flex items-center gap-3">
        {/* State indicator dot */}
        <span
          className={cn(
            'h-2.5 w-2.5 rounded-full flex-shrink-0',
            stateDotStyles[encoder.state]
          )}
        />

        {/* Encoder name */}
        <span className="font-medium text-sm truncate flex-1">
          {encoder.simpleName || encoder.encoderID}
        </span>

        {/* Cycle count */}
        <div className="flex items-center gap-1 text-xs text-muted-foreground">
          <Activity className="h-3 w-3" />
          <span>{cycleCount} cycle{cycleCount !== 1 ? 's' : ''}</span>
        </div>

        {/* Online status */}
        <Badge variant="outline" className="text-xs gap-1">
          <Zap className={cn('h-3 w-3', encoder.online ? 'text-green-500' : 'text-gray-400')} />
          {encoder.online ? 'Online' : 'Offline'}
        </Badge>

        {/* State badge */}
        <Badge
          variant="outline"
          className={cn('text-xs', stateBadgeStyles[encoder.state])}
        >
          {stateLabels[encoder.state]}
        </Badge>

        {/* Pin to Triage / In Triage */}
        {isInTriage ? (
          <Badge variant="secondary" className="text-xs gap-1">
            <Pin className="h-3 w-3" />
            In Triage
          </Badge>
        ) : confirmingPin ? (
          <Button
            variant="default"
            size="sm"
            className="h-7 gap-1 text-xs"
            onClick={handlePinToTriage}
            onBlur={() => setConfirmingPin(false)}
            disabled={isPinning}
            autoFocus
          >
            <Pin className="h-3 w-3" />
            {isPinning ? 'Pinning...' : 'Confirm'}
          </Button>
        ) : (
          <Button
            variant="outline"
            size="sm"
            className="h-7 gap-1 text-xs"
            onClick={() => setConfirmingPin(true)}
          >
            <Pin className="h-3 w-3" />
            Pin to Triage
          </Button>
        )}

        {/* Export logs button */}
        {onExport && (
          <Button
            variant="ghost"
            size="sm"
            className="h-7 w-7 p-0"
            onClick={() => onExport(encoder.encoderID)}
            title="Export logs"
          >
            <FileText className="h-3.5 w-3.5" />
          </Button>
        )}

        {/* View encoder link */}
        <Link
          to="/encoders/$encoderID"
          params={{ encoderID: encoder.encoderID }}
          className={buttonVariants({ variant: 'ghost', size: 'sm', className: 'h-7 w-7 p-0' })}
          title="View encoder"
        >
          <ExternalLink className="h-3.5 w-3.5" />
        </Link>
      </div>

      {/* Details row */}
      <div className="flex items-center gap-2 mt-2 ml-5 text-xs text-muted-foreground">
        <button
          type="button"
          onClick={() => setShowEventLog(!showEventLog)}
          className="flex items-center gap-1 hover:text-foreground transition-colors"
        >
          {showEventLog ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
          <span>
            {encoder.events.length} event{encoder.events.length !== 1 ? 's' : ''}
          </span>
        </button>
        <span className="text-muted-foreground/50">|</span>
        <span>
          Last: {formatDistanceToNow(encoder.lastUpdated, { addSuffix: true })}
        </span>
        {encoder.attachedToLiveStream && (
          <>
            <span className="text-muted-foreground/50">|</span>
            <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200">
              Live Stream
            </Badge>
          </>
        )}
      </div>

      {/* Event log */}
      {showEventLog && encoder.events.length > 0 && (
        <div className="mt-2 ml-5 p-2 bg-zinc-900 rounded text-xs font-mono space-y-0.5 max-h-32 overflow-y-auto border border-zinc-700">
          {encoder.events.map((event, i) => (
            <div key={i} className="flex items-center gap-3">
              <span className="text-zinc-400">
                {format(event.timestamp, 'HH:mm:ss.SSS')}
              </span>
              <span
                className={cn(
                  'font-medium',
                  event.type === 'connect' ? 'text-green-400' : 'text-red-400'
                )}
              >
                {event.type}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
